import React from 'react';
import { useSelect, UseSelectStateChange } from 'downshift';
import { ConversationStyle } from 'Assistant/__generated__/client.schemas';
import { useTranslation } from 'react-i18next';
import {
  Container,
  InputContainer,
  InputText,
  Menu,
  Item,
  ToggleIcon,
  ItemLabel,
  ItemDescription,
  ItemText,
  SelectedIcon,
  LoadingIndicator,
  LoadingContainer,
} from './styles';
import { StyleIcon } from './StyleIcon';

const conversationStyles: ConversationStyle[] = [
  ConversationStyle.Active_Listening,
  ConversationStyle.Coaching,
  ConversationStyle.Practical_Tools,
];

type StyleSelectProps = {
  default?: ConversationStyle;
  onHomeScreen?: boolean;
  isLoading?: boolean;
  onStyleChange?(style: ConversationStyle): void;
};

function BaseStyleSelect({
  default: defaultStyle,
  onHomeScreen,
  onStyleChange,
}: Omit<StyleSelectProps, 'isLoading'>) {
  const { t: translate } = useTranslation('chatbot');
  const handleSelectedStyleChange = (
    changes: UseSelectStateChange<ConversationStyle>,
  ) => {
    if (changes.selectedItem && onStyleChange) {
      onStyleChange(changes.selectedItem);
    }
  };

  const styleToID = (style: ConversationStyle) =>
    style.replace(/ /g, '_').toLowerCase();

  const styleToDisplayLabel = (style: ConversationStyle | null) => {
    if (!style) {
      return '';
    }

    if (style === ConversationStyle.Active_Listening) {
      return translate(
        'conversation.conversation_style_select.styles.active_listening.label',
      );
    }

    if (style === ConversationStyle.Coaching) {
      return translate(
        'conversation.conversation_style_select.styles.coaching.label',
      );
    }

    if (style === ConversationStyle.Practical_Tools) {
      return translate(
        'conversation.conversation_style_select.styles.practical_tools.label',
      );
    }

    throw new Error('Unknown conversation style');
  };

  const styleToDisplayDescription = (style: ConversationStyle) => {
    if (style === ConversationStyle.Active_Listening) {
      return translate(
        'conversation.conversation_style_select.styles.active_listening.description',
      );
    }

    if (style === ConversationStyle.Coaching) {
      return translate(
        'conversation.conversation_style_select.styles.coaching.description',
      );
    }

    if (style === ConversationStyle.Practical_Tools) {
      return translate(
        'conversation.conversation_style_select.styles.practical_tools.description',
      );
    }

    throw new Error('Unknown conversation style');
  };

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
  } = useSelect({
    items: conversationStyles,
    defaultSelectedItem: defaultStyle,
    onSelectedItemChange: handleSelectedStyleChange,
  });

  return (
    <Container>
      <InputContainer
        {...getToggleButtonProps()}
        aria-label={translate('conversation.conversation_style_select.label')}
      >
        <StyleIcon style={selectedItem} />
        <InputText>{styleToDisplayLabel(selectedItem)}</InputText>
        <ToggleIcon aria-hidden={true} $isOpen={isOpen} />
      </InputContainer>
      <Menu {...getMenuProps()} $isOpen={isOpen} $onHomeScreen={onHomeScreen}>
        {isOpen && (
          <>
            {conversationStyles.map(style => (
              <Item
                key={styleToID(style)}
                {...getItemProps({ item: style })}
                aria-label={styleToDisplayLabel(style)}
                aria-describedby={`${styleToID(style)}-description`}
              >
                <StyleIcon style={style} />
                <ItemText>
                  <ItemLabel>{styleToDisplayLabel(style)}</ItemLabel>
                  <ItemDescription id={`${styleToID(style)}-description`}>
                    {styleToDisplayDescription(style)}
                  </ItemDescription>
                </ItemText>
                {selectedItem === style && <SelectedIcon aria-hidden={true} />}
              </Item>
            ))}
          </>
        )}
      </Menu>
    </Container>
  );
}

function StyleSelect({
  default: defaultStyle = ConversationStyle.Coaching,
  onHomeScreen = false,
  isLoading = false,
  onStyleChange,
}: StyleSelectProps) {
  const { t: translate } = useTranslation('chatbot');
  if (isLoading) {
    return (
      <Container>
        <LoadingContainer
          aria-busy={true}
          aria-live="polite"
          aria-label={translate(
            'conversation.conversation_style_select.loading_label',
          )}
        >
          <LoadingIndicator />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <BaseStyleSelect
      default={defaultStyle}
      onHomeScreen={onHomeScreen}
      onStyleChange={onStyleChange}
    />
  );
}

export { StyleSelect };
export type { StyleSelectProps };
